import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getSubscriptionRedirectPath } from '../utils/subscriptionValidation';

interface SubscriptionProtectedRouteProps {
  children: React.ReactNode;
}

const SubscriptionProtectedRoute: React.FC<SubscriptionProtectedRouteProps> = ({
  children,
}) => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasValidSubscription, loading: subLoading } = useSubscription();

  useEffect(() => {
    // Wait for both auth and subscription to load
    if (authLoading || subLoading) {
      return;
    }

    // Check authentication
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    // Check subscription validity
    if (!hasValidSubscription()) {
      history.push('/subscription');
      return;
    }
  }, [isAuthenticated, authLoading, subLoading, hasValidSubscription, history]);

  // Loading state
  if (authLoading || subLoading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner mx-auto mb-6 glow'></div>
          <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
            {t('verifyingSubscription')}
          </p>
        </div>
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
};

export default SubscriptionProtectedRoute;
